import api from "@/plugins/api";
export async function listForms(params) {
    const response = await api.get("/portal/collecte/forms", {
        unwrapEnvelope: true,
        params: {
            page: params?.page,
            perPage: params?.perPage,
            campaignId: params?.campaignId,
        },
    });
    return response.data;
}
export async function createForm(body) {
    const response = await api.post("/portal/collecte/forms", body, { unwrapEnvelope: true });
    return response.data;
}
// A campaign carries at most one live form, and the two ways of naming one are handled
// asymmetrically upstream: a SUPPLIED `campaignId` on a taken campaign is refused with 409
// `Form.Campaign.AlreadyHasForm`; an OMITTED one inherits the source's campaign only if it is free
// and otherwise yields a campaign-less copy with 201. Other recoverable codes: 409
// `Form.PublicSlug.Conflict`, 422 `Form.PublicSlug.*` / `Form.Title.*`, 404 `Form.NotFound`.
export async function duplicateForm(id, body) {
    const response = await api.post(`/portal/collecte/forms/${id}/duplicate`, body, {
        unwrapEnvelope: true,
    });
    return response.data;
}
// Admin label only: `publicSlug` is untouched and a Published form gains no unpublished changes.
// The returned `title` is the authority (the backend trims). 409 `Form.NotEditable`, 404
// `Form.NotFound`, 422 `Form.Title.Required` / `Form.Title.TooLong`.
export async function renameForm(id, title) {
    const payload = { title };
    const response = await api.put(`/portal/collecte/forms/${id}/title`, payload, { unwrapEnvelope: true });
    return response.data;
}
// The id can be a router param, which arrives percent-DECODED, so it is re-encoded into one inert
// path segment (a no-op for a well-formed uuid).
export async function getForm(id) {
    const response = await api.get(`/portal/collecte/forms/${encodeURIComponent(id)}`, { unwrapEnvelope: true });
    return response.data;
}
// Replace-whole. The list travels under the `eligibleProjects` key: a body sent under another key
// clears the collection on a deceptive 200. 422 `Form.EligibleProjects.Invalid`, 409 `Form.NotEditable`.
export async function setEligibleProjects(id, projects) {
    const response = await api.put(`/portal/collecte/forms/${id}/eligible-projects`, { eligibleProjects: projects }, { unwrapEnvelope: true });
    return response.data;
}
// Replace-whole, server-side ids omitted on the rows. 422 `Form.Frequencies.Invalid`, 409
// `Form.NotEditable`.
export async function setFrequencies(id, frequencies) {
    const response = await api.put(`/portal/collecte/forms/${id}/frequencies`, { frequencies }, { unwrapEnvelope: true });
    return response.data;
}
// Replace-whole, and the list travels under the `amountPresets` key (not `presets`). 422
// `Form.AmountPresets.Invalid` / `Form.AmountPresets.NotWholeEuro`, 409 `Form.NotEditable`.
export async function setAmountPresets(id, presets) {
    const response = await api.put(`/portal/collecte/forms/${id}/amount-presets`, { amountPresets: presets }, { unwrapEnvelope: true });
    return response.data;
}
// Replace-whole. 422 `Form.Messages.Invalid`, 409 `Form.NotEditable`.
export async function setMessages(id, messages) {
    const response = await api.put(`/portal/collecte/forms/${id}/messages`, { messages }, { unwrapEnvelope: true });
    return response.data;
}
// The Branding object travels at the body root, not wrapped. 422 `Form.Branding.Invalid`, 409
// `Form.NotEditable`.
export async function updateBranding(id, branding) {
    const response = await api.put(`/portal/collecte/forms/${id}/branding`, branding, { unwrapEnvelope: true });
    return response.data;
}
// `null` clears the campaign; the id is stored verbatim, with no existence check upstream. 422
// `Form.CampaignId.Invalid`, 409 `Form.Campaign.RequiredWhenPublished`, 409 `Form.Campaign.AlreadyHasForm`,
// 409 `Form.NotEditable`.
export async function setCampaign(id, campaignId) {
    const payload = { campaignId };
    const response = await api.put(`/portal/collecte/forms/${id}/campaign`, payload, { unwrapEnvelope: true });
    return response.data;
}
// Replace-whole: a field the caller omits is CLEARED, so every caller must send the complete
// object (the two tabs writing this endpoint carry every field of `store.form.settings`).
// `allowedEmbedOrigins` rides the body unedited. 422 `Form.Settings.EmbedOrigins.Invalid` /
// `Form.Settings.MinimumAmount.Invalid` / `Form.Settings.MinimumAmount.NotWholeEuro`, 409
// `Form.NotEditable`.
export async function updateSettings(id, settings) {
    const response = await api.put(`/portal/collecte/forms/${id}/settings`, settings, { unwrapEnvelope: true });
    return response.data;
}
// The upload accepts png / jpeg / webp only (the backend sniffs the server-side MIME; SVG is refused
// as an XSS vector). The part's MIME is derived from the extension because `file.type` is empty or
// `application/octet-stream` for many sources.
const VISUAL_CONTENT_TYPES = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
};
export class UnsupportedVisualTypeError extends Error {
    constructor(extension) {
        super(`Unsupported visual type: .${extension}`);
        Object.defineProperty(this, "extension", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: extension
        });
        this.name = "UnsupportedVisualTypeError";
    }
}
function resolveVisualContentType(file) {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    const mime = VISUAL_CONTENT_TYPES[ext];
    if (!mime) {
        throw new UnsupportedVisualTypeError(ext);
    }
    return mime;
}
// Non-mutating: stores the image and returns its URL, which the caller persists through the
// matching setter (`updateBranding`, `setAmountPresets`).
export async function uploadVisual(id, file) {
    const contentType = resolveVisualContentType(file);
    const typedFile = new File([file], file.name, { type: contentType });
    const form = new FormData();
    form.append("file", typedFile);
    const response = await api.post(`/portal/collecte/forms/${id}/visuals`, form, {
        unwrapEnvelope: true,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.url;
}
// Every call freezes a fresh version — the backend does NOT gate on `hasUnpublishedChanges`. 409
// `Form.InvalidTransition` / `Form.Publish.Conflict`, 422 `Form.Publish.UnresolvableProject`.
// The lifecycle POSTs send no body, so the per-request config is the THIRD argument: passed second
// it would be sent as the payload.
export async function publishForm(id) {
    const response = await api.post(`/portal/collecte/forms/${id}/publish`, undefined, { unwrapEnvelope: true });
    return response.data;
}
// 409 `Form.InvalidTransition` (Published only).
export async function pauseForm(id) {
    const response = await api.post(`/portal/collecte/forms/${id}/pause`, undefined, { unwrapEnvelope: true });
    return response.data;
}
// 409 `Form.InvalidTransition` (Paused only).
export async function resumeForm(id) {
    const response = await api.post(`/portal/collecte/forms/${id}/resume`, undefined, { unwrapEnvelope: true });
    return response.data;
}
// Terminal. A falsy `redirectToFormId` sends no payload at all (the upstream body is optional). The
// upstream preflights only the two permanently dead targets (no such row, itself Archived); any
// other target state is re-derived on every public read, so offering published forms only stays
// the picker's job. 409 `Form.InvalidTransition`, 422 `Form.RedirectToFormId.Invalid` /
// `.SelfReference` / `.NotAvailable`, 404 `Form.NotFound`.
export async function archiveForm(id, redirectToFormId) {
    // Trimmed first, so a whitespace-only target is "no redirect" here rather than upstream.
    const target = redirectToFormId?.trim();
    const body = target
        ? { redirectToFormId: target }
        : undefined;
    const response = await api.post(`/portal/collecte/forms/${id}/archive`, body, { unwrapEnvelope: true });
    return response.data;
}
// Newest first; `perPage` max 100, backend default 20. 400 `Form.Version.Invalid`.
export async function listVersions(id, params) {
    const response = await api.get(`/portal/collecte/forms/${id}/versions`, {
        unwrapEnvelope: true,
        params: { page: params?.page, perPage: params?.perPage },
    });
    return response.data;
}
// 404 `Form.Version.NotFound`, 400 `Form.Version.Invalid`.
export async function getVersion(id, versionNumber) {
    const response = await api.get(`/portal/collecte/forms/${id}/versions/${versionNumber}`, { unwrapEnvelope: true });
    return response.data;
}
// No query parameters; a traffic-less form answers 200 with zero counters, never 404. The id is a
// router param and arrives percent-DECODED (`..%2F..%2Fadmin` matches `:id`), so it is re-encoded
// into one inert segment. 400 `Form.Id.Invalid`, 404 `Form.NotFound`.
export async function getFormDashboard(id) {
    const response = await api.get(`/portal/collecte/forms/${encodeURIComponent(id)}/dashboard`, { unwrapEnvelope: true });
    return response.data;
}
